import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Switch,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  LocalOffer as CouponIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import couponService from '../services/couponService';
import { Coupon, CouponFilters } from '../types/coupon';
import CreateCouponDialog from '../components/Coupons/CreateCouponDialog';
import CouponUsageDialog from '../components/Coupons/CouponUsageDialog';
import { PageHeader, ResponsiveTable, StatCard } from '../components/common';
import { Grid } from '../components/common/TypedGrid';

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<CouponFilters>({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [showUsageDialog, setShowUsageDialog] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);
      
      // Clean filters to remove undefined values
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key as keyof CouponFilters] = value;
        }
        return acc;
      }, {} as CouponFilters);

      // Add search query if present
      if (searchQuery.trim()) {
        cleanFilters.search = searchQuery.trim();
      }

      console.log('loadCoupons with cleanFilters:', cleanFilters);
      console.log('raw filters:', filters);
      console.log('searchQuery:', searchQuery);

      // Load coupons first
      const couponsData = await couponService.getCoupons(cleanFilters);
      setCoupons(couponsData);

      // Load analytics separately with error handling
      try {
        const analyticsData = await couponService.getCouponAnalytics();
        setAnalytics(analyticsData);
      } catch (analyticsError) {
        console.warn('Failed to load analytics:', analyticsError);
        setAnalytics(null);
      }

    } catch (error) {
      console.error('Error loading coupons:', error);
      showSnackbar('Failed to load coupons', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, couponId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCouponId(couponId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCouponId(null);
  };

  const handleToggleActive = async (coupon: Coupon) => {
    try {
      await couponService.updateCoupon(coupon.id, { isActive: !coupon.isActive });
      showSnackbar(`Coupon ${!coupon.isActive ? 'activated' : 'deactivated'} successfully`);
      loadCoupons();
    } catch (error) {
      showSnackbar('Failed to update coupon status', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    
    try {
      await couponService.deleteCoupon(deleteConfirmId);
      showSnackbar('Coupon deleted successfully');
      setDeleteConfirmId(null);
      loadCoupons();
    } catch (error) {
      showSnackbar('Failed to delete coupon', 'error');
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    showSnackbar('Coupon code copied to clipboard');
  };

  const handleExport = async () => {
    try {
      const csv = await couponService.exportCoupons(filters);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coupons_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showSnackbar('Coupons exported successfully');
    } catch (error) {
      showSnackbar('Failed to export coupons', 'error');
    }
  };

  const getStatusChip = (coupon: Coupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.validFrom);
    const validUntil = new Date(coupon.validUntil);

    if (!coupon.isActive) {
      return <Chip label="Inactive" size="small" color="default" />;
    } else if (now < validFrom) {
      return <Chip label="Scheduled" size="small" color="info" />;
    } else if (now > validUntil) {
      return <Chip label="Expired" size="small" color="error" />;
    } else {
      return <Chip label="Active" size="small" color="success" />;
    }
  };

  const getDiscountText = (coupon: Coupon) => {
    if (coupon.type === 'flat') {
      return `₹${coupon.value} OFF`;
    } else {
      return `${coupon.value}% OFF${coupon.maxDiscount ? ` (Max ₹${coupon.maxDiscount})` : ''}`;
    }
  };

  return (
    <Box>
      <PageHeader
        title="Coupons"
        subtitle="Create and manage discount coupons for your customers"
        actions={
          <>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
            >
              Create Coupon
            </Button>
          </>
        }
      />

      {analytics && (
        <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard label="Total Coupons" value={analytics.totalCoupons} icon={<CouponIcon />} tone="primary" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Active Coupons" value={analytics.activeCoupons} icon={<TrendingUpIcon />} tone="success" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Times Used" value={analytics.totalUsage} icon={<PeopleIcon />} tone="info" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label="Total Discount"
              value={`₹${analytics.totalDiscountGiven.toLocaleString()}`}
              icon={<MoneyIcon />}
              tone="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code, title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All"
                onClick={() => setFilters({})}
                color={Object.keys(filters).length === 0 ? 'primary' : 'default'}
              />
              <Chip
                label="Active"
                onClick={() => setFilters(prev => ({
                  ...prev,
                  isActive: prev.isActive === true ? undefined : true
                }))}
                color={filters.isActive === true ? 'primary' : 'default'}
              />
              <Chip
                label="Featured"
                onClick={() => setFilters(prev => ({
                  ...prev,
                  isFeatured: prev.isFeatured === true ? undefined : true
                }))}
                color={filters.isFeatured === true ? 'primary' : 'default'}
              />
              <Chip
                label="Flat Discount"
                onClick={() => setFilters(prev => ({
                  ...prev,
                  type: prev.type === 'flat' ? undefined : 'flat'
                }))}
                color={filters.type === 'flat' ? 'primary' : 'default'}
              />
              <Chip
                label="Percentage"
                onClick={() => setFilters(prev => ({
                  ...prev,
                  type: prev.type === 'percentage' ? undefined : 'percentage'
                }))}
                color={filters.type === 'percentage' ? 'primary' : 'default'}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Coupons Table */}
      <Card>
        <ResponsiveTable
          columns={[
            {
              key: 'code',
              header: 'Code',
              mobile: 'title',
              render: (coupon: Coupon) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                    {coupon.code}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyCode(coupon.code);
                    }}
                    aria-label={`Copy code ${coupon.code}`}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              ),
            },
            {
              key: 'title',
              header: 'Title',
              mobile: 'subtitle',
              render: (coupon: Coupon) => (
                <>
                  <Typography variant="body2" fontWeight={500}>
                    {coupon.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {coupon.description}
                  </Typography>
                </>
              ),
            },
            {
              key: 'discount',
              header: 'Discount',
              render: (coupon: Coupon) => (
                <>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {getDiscountText(coupon)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Min order: ₹{coupon.minOrderValue}
                  </Typography>
                </>
              ),
            },
            {
              key: 'usage',
              header: 'Usage',
              align: 'center',
              render: (coupon: Coupon) => (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUsageDialog(coupon.id);
                  }}
                >
                  {coupon.usageCount} / {coupon.usageLimit}
                </Button>
              ),
            },
            {
              key: 'validity',
              header: 'Validity',
              render: (coupon: Coupon) => (
                <Typography variant="caption">
                  {format(new Date(coupon.validFrom), 'dd MMM')} –{' '}
                  {format(new Date(coupon.validUntil), 'dd MMM yyyy')}
                </Typography>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              align: 'center',
              render: (coupon: Coupon) => getStatusChip(coupon),
            },
            {
              key: 'featured',
              header: 'Featured',
              align: 'center',
              render: (coupon: Coupon) => (
                <Switch
                  size="small"
                  checked={coupon.isFeatured}
                  inputProps={{ 'aria-label': `Feature ${coupon.code}` }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={async () => {
                    await couponService.updateCoupon(coupon.id, {
                      isFeatured: !coupon.isFeatured,
                    });
                    loadCoupons();
                  }}
                />
              ),
            },
            {
              key: 'actions',
              header: 'Actions',
              align: 'right',
              mobile: 'actions',
              render: (coupon: Coupon) => (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuClick(e, coupon.id);
                  }}
                  aria-label="Coupon actions"
                >
                  <MoreIcon />
                </IconButton>
              ),
            },
          ]}
          rows={coupons}
          rowKey={(coupon) => coupon.id}
          loading={loading}
          aria-label="Coupons"
          emptyTitle="No coupons yet"
          emptyDescription="Create a coupon to run a discount or promotion."
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const coupon = coupons.find(c => c.id === selectedCouponId);
          if (coupon) {
            setEditingCoupon(coupon);
            setShowCreateDialog(true);
          }
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          const coupon = coupons.find(c => c.id === selectedCouponId);
          if (coupon) {
            handleToggleActive(coupon);
          }
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> 
          {coupons.find(c => c.id === selectedCouponId)?.isActive ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem onClick={() => {
          setDeleteConfirmId(selectedCouponId);
          handleMenuClose();
        }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      {showCreateDialog && (
        <CreateCouponDialog
          open={showCreateDialog}
          onClose={() => {
            setShowCreateDialog(false);
            setEditingCoupon(null);
          }}
          onSuccess={() => {
            setShowCreateDialog(false);
            setEditingCoupon(null);
            loadCoupons();
            showSnackbar(editingCoupon ? 'Coupon updated successfully' : 'Coupon created successfully');
          }}
          editingCoupon={editingCoupon}
        />
      )}

      {/* Usage Dialog */}
      {showUsageDialog && (
        <CouponUsageDialog
          open={Boolean(showUsageDialog)}
          onClose={() => setShowUsageDialog(null)}
          couponId={showUsageDialog}
        />
      )}

      {/* Delete Confirmation */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Coupon</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this coupon? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponManagement;